<template>
  <div>
    <!-- Version/Released/End of life (and Status, only while editing) all in
         a single row — same compact rhythm as AttachDependencyForm.vue,
         since this replaces a table row in place rather than opening below it. -->
    <div class="grid grid-cols-2 gap-2.5" :class="form.editingId ? 'sm:grid-cols-4' : 'sm:grid-cols-3'">
      <div>
        <label class="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Version<span class="text-red-500">*</span></label>
        <input ref="versionInput" v-model="form.version" type="text" placeholder="e.g. 16.4" autocomplete="off"
          class="w-full h-[31px] px-2.5 py-0 text-sm leading-4 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 placeholder:text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
      </div>
      <div>
        <label class="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Released <span class="normal-case font-normal text-gray-400 dark:text-gray-500">(optional)</span></label>
        <DatePicker v-model="form.releasedDate" input-class="h-[31px] px-2.5 py-0 text-sm leading-4 rounded-md placeholder:text-xs" />
      </div>
      <div>
        <div class="flex items-center justify-between gap-2 mb-1">
          <label class="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">End of life <span class="normal-case font-normal text-gray-400 dark:text-gray-500">(optional)</span></label>
          <label class="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap cursor-pointer">
            <input v-model="form.eolDateNotAnnounced" type="checkbox" @change="handleEolNotAnnouncedChange"
              class="w-3 h-3 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer" />
            Not announced
          </label>
        </div>
        <DatePicker v-model="form.eolDate" :disabled="form.eolDateNotAnnounced" input-class="h-[31px] px-2.5 py-0 text-sm leading-4 rounded-md placeholder:text-xs" />
      </div>

      <!-- Status: only meaningful once a version already exists — a fresh Add
           is always created pre-approved server-side (see the admin
           controller's create action), so there's nothing to moderate yet.
           Changing it fires immediately (a separate approve/reject/unapprove
           endpoint, not bundled into Save), same as the row-level control it replaces. -->
      <div v-if="form.editingId">
        <label class="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Status</label>
        <select :value="currentStatus" @change="$emit('change-status', $event.target.value)"
          class="w-full h-[31px] text-xs font-semibold rounded-md pl-2 pr-6 border focus:outline-none focus:ring-1 focus:ring-blue-500 transition cursor-pointer"
          :class="statusSelectClass(currentStatus)">
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <!-- Never manually chosen — a version only ends up here
               automatically, inherited from its parent component being
               marked org specific (see OrgComponentVersionsController). Kept
               selectable-looking but disabled purely so the dropdown
               correctly displays it when it's the current value, rather than
               showing nothing selected. -->
          <option value="org_specific" disabled>Org specific</option>
        </select>
      </div>
    </div>

    <p v-if="localDateError" class="text-[11px] text-red-500 dark:text-red-400 font-medium mt-2">The release date must be before the end-of-life date.</p>
    <p v-else-if="form.error" class="text-[11px] text-red-500 dark:text-red-400 font-medium mt-2">{{ form.error }}</p>

    <div class="flex gap-2 justify-end mt-2.5">
      <button type="button" @click="$emit('cancel')"
        class="px-2.5 py-1 text-[11px] font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md cursor-pointer">
        Cancel
      </button>
      <button type="button" @click="$emit('save')" :disabled="form.saving || !form.version.trim() || localDateError"
        class="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-md cursor-pointer">
        {{ form.saving ? 'Saving...' : (form.editingId ? 'Save' : 'Add') }}
      </button>
    </div>
  </div>
</template>

<script setup>
// form is the parent's versionForm ref value, passed through as one object so
// this stays a single source of truth — mutating its fields here (via
// v-model) is intentional.
import { ref, computed, nextTick, onMounted } from 'vue'
import DatePicker from 'controllers/vue_components/shared/DatePicker.vue'

const props = defineProps({
  form: { type: Object, required: true },
  // Status lives on the version record itself, not on the editable form
  // object — it changes immediately on select (a separate API call), so
  // binding to the live record avoids the form's own state ever going stale
  // relative to what actually got saved.
  currentStatus: { type: String, default: 'pending' },
})

defineEmits(['save', 'cancel', 'change-status'])

const versionInput = ref(null)

const localDateError = computed(() =>
  !!props.form.releasedDate && !!props.form.eolDate && props.form.releasedDate >= props.form.eolDate)

// Checking the box and having a real date are mutually exclusive — clear the
// date so a stale value can never be resubmitted alongside the flag.
function handleEolNotAnnouncedChange() {
  if (props.form.eolDateNotAnnounced) props.form.eolDate = ''
}

const STATUS_SELECT_CLASSES = {
  pending: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800',
  approved: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
  rejected: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
  org_specific: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
}
function statusSelectClass(status) {
  return STATUS_SELECT_CLASSES[status] ?? 'bg-gray-100 dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-700'
}

// Adding brand new (not editing) auto-focuses; editing an existing version
// does not, matching GlobalComponentVersionsSection's prior behavior.
onMounted(() => {
  if (!props.form.editingId) nextTick(() => versionInput.value?.focus())
})
</script>
